// ---------------------------------------------------------------------------
// Static mock M&A briefing for the /m-a landing page
// No database, no API calls — pure client-side content
//
// Fictional company: Auriga Cybershield GmbH (MDR + Cyber-Insurance Bundle)
// NOT based on any real company. All persons, investors, partners, contract
// names and metrics are fictional. Structural narrative composes typical M&A
// risk patterns for illustration only.
// ---------------------------------------------------------------------------

export const DEMO_MA_COMPANY_NAME = 'Auriga Cybershield GmbH';

export function getDemoMaDate(): string {
  return new Date().toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * Full demo M&A briefing in Markdown format.
 * Mirrors the 14-section Pre-DD structure produced by the real synthesis agent.
 */
export function getDemoMaBriefingMarkdown(): string {
  const today = getDemoMaDate();

  return `# M&A Pre-Due-Diligence Briefing — Auriga Cybershield GmbH

**Erstellt:** ${today}   **Klassifikation:** Vertraulich   **Typ:** Cybersecurity / InsurTech   **Deal Attractiveness Score:** 6,3 / 10

---

## 1. Executive Summary

| Feld | Wert |
|---|---|
| **Firmenname** | Auriga Cybershield GmbH (100%-Tochter der Auriga Cybershield ApS, Kopenhagen DK) |
| **Rechtsform** | GmbH |
| **Sitz** | Ballindamm 27, 20095 Hamburg \| operativ: Lübeck + Kopenhagen |
| **HRB** | 99281, Amtsgericht Hamburg \| USt-IdNr.: DE412785963 |
| **Gründung** | GmbH: 15.05.2023 (orig. Lübeck); Konzernmutter Auriga Cybershield ApS: Juni 2020 |
| **Stammkapital** | €25.000 (reines Vertriebsvehikel; IP, SOC, Kapital in DK-ApS) |
| **GF Deutschland** | Gründer C (COO Konzern, ab 12.09.2025) |
| **Hauptgesellschafter** | Auriga Holding ApS 100% der GmbH; Konzern: Lead-Investor der Series B (Lead Series B), Lead-Investor der Series A, Lead-Investor der Seed-Runde, PE-Co-Investor aus UK; Gründer-Mehrheit |
| **Mitarbeiter (Gruppe)** | ~230 (Sep 2025) — +5,2× in 14 Monaten nach Series B |
| **Töchter / Schwester-Ges.** | Auriga Cybershield ApS (DK, operativ), Auriga Underwriting ApS (DK, MGA, FSA-Lizenz 23744-A), Auriga Cybershield AB (SE, vermutet) |
| **Umsatz ARR (Schätzung)** | ~$10–13M (Sep 2025) \| ~90% YoY-Wachstum (CEO-Claim, unbewiesen) |
| **EBITDA** | Negativ (Wachstumsphase — Personalkosten ~€15M p.a. > geschätzter Umsatz) |
| **Gesamtfinanzierung** | €48–52M über 3 Runden (Seed 2021, Series A 2022, Series B März 2024) |
| **Bewertungsindikation EV** | $60–130M / €55–119M (Korridor, basis ARR 6–10× Multiple) |
| **Datenqualität** | SOLIDE — Score 7/10: DK-CVR-Jahresabschluss 2023 + Bundesanzeiger + 14 Fachquellen ausgewertet |

### Top-3-Erkenntnisse (mit Deal-Implikation)

**Einzigartiges MDR + Cyberversicherungs-Bundle — kein direkter europäischer Wettbewerber**
Auriga Cybershield ist der einzige nordeuropäische Anbieter, der 24/7-Managed XDR und eine integrierte Cyberversicherung (via Auriga Underwriting ApS) aus einer Hand anbietet. Cyber-MGA-Wettbewerber (DE, 2023 restrukturiert) versuchte ein ähnliches Modell und scheiterte — Aurigas Ausführung muss in der DD kritisch geprüft werden.
→ **So-What:** Strategischer Wert für Käufer aus Versicherungs- oder Plattform-Cybersecurity-Sektor; schwer replizierbar; Insurance-Bundle schafft Cross-Sell-Flywheel.

**~2.700% Umsatzwachstum in 4 Jahren (Deloitte Fast 50 DACH, Rang 5 2024) + NIS2-Rückenwind**
Hypergrowth-Pfad extern validiert. NIS2 (seit Dezember 2025 in DE in Kraft) verpflichtet ~29.500 deutsche Unternehmen neu — genau im SME/Mid-Market-Zielkundensegment. DORA und das KRITIS-Dachgesetz verstärken den strukturellen Nachfragepull.
→ **So-What:** Regulatorischer Rückenwind für 3–5 Jahre; Timing für Einstieg sehr relevant.

**Q4 2024: Deutschland überholt Skandinavien als Hauptmarkt**
CEO Gründer A bestätigte im November 2024 in einem Fachinterview, dass der deutsche Markt erstmals mehr Umsatz generiert als die nordischen Heimatländer (DK/SE/NO). DACH ist der primäre Wachstumsmotor.
→ **So-What:** Strategische Hebelwirkung des deutschen Marktes; Käufer mit DACH-Bestandsnetz hat signifikanten Synergievorteil.

### Top-3-Risiken

**⚠ Lückenhafte Finanztransparenz [HOCH]**
Der dänische DK-CVR-Jahresabschluss 2023 ist verfügbar (Umsatz €11,2M, Personalaufwand €9,8M, Verlust €4,1M). GmbH-Abschluss steht im Bundesanzeiger noch aus. ARR-Wachstum 2024–2025, EBITDA, Burn-Rate, Cap Table und Vertragslaufzeiten bleiben Schätzungen mit ±15% Konfidenz. ARR/MA-Ratio liegt 45% unter dem MDR-Branchenbenchmark (~$45–55K vs. $80–120K) — Kaufpreisfindung erfordert verifizierte 2025-Zahlen.

**⚠ Dreifaches Key-Man-Risiko der Gründer [KRITISCH]**
Gründer A (CEO, Investor-Relations, 9/10), Gründer B (CTO, IP, MSRC #2, 9/10), Gründer C (COO/GF-DE, 7/10) sind alle ex-Nachrichtendienst (dänischer Auslandsnachrichtendienst) und unersetzlich. Kein Succession Plan identifiziert. Kein Deal ohne Retention-Vereinbarungen aller drei Gründer.

**⚠ Hauptrisikoträger-Abhängigkeit als Single-Point-of-Failure [HOCH]**
Auriga Underwriting ApS ist als MGA existenziell von Hauptrisikoträger (UK) als Risikoträger abhängig. Vertragslaufzeit, Kündigungsfristen und Change-of-Control-Klauseln sind nicht öffentlich. Ein CoC-Trigger bei Akquisition könnte den gesamten Insurance-USP eliminieren — dies ist der kritischste Einzelpunkt vor LOI.

**Empfehlung:** Mit erheblichen Vorbehalten weiterverfolgen. Vor einem LOI zwingend: (1) verifizierte 2024/2025-Finanzdaten via NDA, (2) Auszug aus DK-CVR mit Cap Table und Liquidation Preferences, (3) Hauptrisikoträger-Vertragskonditionen.

### Verifizierte Signale aus der Recherche

Über die strukturelle Analyse hinaus hat die Pipeline fünf öffentlich verifizierbare Signale identifiziert, die einen frühen Informationsvorsprung schaffen:

**Markenanmeldung „AurigaShield AI" (EUIPO, 14.01.2026)**
Klassen 9 (Sicherheitssoftware) und 42 (SaaS). Indiziert ein Q2/Q3-2026-Produktlaunch in Richtung KI-getriebener MDR-Stack. → DD: produktstrategischer Umfang, R&D-Investitionsbudget, Differenzierung zu CrowdStrike Charlotte AI und Microsoft Security Copilot.

**Versteckte Tochtergesellschaft: Auriga Underwriting Belgium SA**
Gegründet 12.11.2024 in Brüssel (BCE-Reg-Nr. 0795.123.456, Stammkapital €18.500). In keiner öffentlichen Mitteilung des Konzerns erwähnt — vermutlich Vorbereitung der BeNeLux-Underwriting-Expansion außerhalb der dänischen FSA-Lizenz. → DD: ist diese Einheit bilanzkonsolidiert? Welche regulatorische Strategie (NBB-Lizenzantrag laufend)?

**Job-Posting-Verschiebung: regulierter Finanzsektor (Q4 2025)**
23 offene Stellen Stand Oktober 2025 (LinkedIn + Indeed), davon 14 in DE. Auffällig: drei Mid-Market Account Executive-Rollen mit Branchen-Fokus „Banking / Insurance" — eine Verschiebung weg vom bisherigen Industrie-/Logistik-Schwerpunkt. → DD: aktive Pipeline im BaFin-/DORA-regulierten Segment? Hat das Produkt die nötige Tiefe (BSI C5, ISAE 3402)?

**LP-Brief-Erwähnung des Lead-Investors (Q3 2025)**
Im öffentlich zugänglichen Q3-2025-LP-Brief des Lead-Investors wird Auriga als „Top-3 performance asset of the current fund vintage" genannt — interpretierbar als Vorbereitung für Sekundärtransaktion oder Trade-Sale-Sondierung. → DD: laufende Exit-Gespräche oder bereits Term-Sheets in Verhandlung? Stimmt das mit den Burn Rate und Runway-Signalen überein?

**GitHub-Aktivitätsverlagerung: ML-First-Pivot**
Public-Repo „auriga-detection-rules" (12 Mitwirkende) ohne Commits seit Mai 2025. Parallel: „auriga-ml-pipeline" mit +340% Commit-Aktivität (61 → 269 Commits/Quartal). Strategische Verschiebung von regelbasierter zu ML-basierter Detection — kongruent zur Markenanmeldung „AurigaShield AI". → DD: ML-Talente an Bord, Trainingsdaten-Lizenzlage geklärt, Patentstrategie?

---

## 2. Unternehmensprofil

### 2.1 Firmendaten & Gesellschafterstruktur

| Merkmal | Detail |
|---|---|
| Firma | Auriga Cybershield GmbH |
| HRB | 99281, Amtsgericht Hamburg |
| USt-IdNr. | DE412785963 |
| Sitz (Registered) | Ballindamm 27, 20095 Hamburg (c/o Spaces Hamburg) |
| Operativer Standort DE | Lübeck (vertrieblich), Kopenhagen (technisch/SOC) |
| Stammkapital | €25.000 |
| Gründungsdatum GmbH | 15.05.2023 |
| Branche | IT-Sicherheitsdienstleistungen / Managed Detection & Response |
| Klassifizierung | Sonstige IT-Dienstleistungen (Creditreform) |
| Unternehmensgegenstand | Integrierter Cybersecurity-Service (MDR/MXDR, SOC, IR) + gebündelte Cyberversicherung via Auriga Underwriting ApS |

### 2.2 Handelsregister-Chronologie (HRB 99281)

| Datum | Event |
|---|---|
| 15.05.2023 | Gründung in Lübeck, GF: Ex-GF DE (heute CCO), Stammkapital €25.000 |
| 11.10.2023 | Sitzverlegung: Lübeck → Hamburg |
| 22.10.2023 | HR-Eintragung Sitzverlegung |
| 12.09.2025 | GF-Wechsel: Ex-GF DE (heute CCO) → Gründer C (Co-Gründer & COO) |

**Interpretation:** Die deutsche GmbH ist ein reines Vertriebsvehikel (€25.000 Stammkapital). IP, Kapital, Kundenverträge und Versicherungslizenz liegen in der dänischen ApS. Der GF-Wechsel nach gut 2 Jahren deutet auf strategische Neuausrichtung (Co-Gründer übernimmt DE-Verantwortung) oder operative Herausforderungen des Vorgängers hin. In DD klären.

### 2.3 UBO-Kette & Gesellschafterstruktur

**UBO-Transparenz:** UNGENÜGEND — Auszug aus DK-CVR ist Must-Have vor LOI

\`\`\`
Auriga Cybershield GmbH (DE, HRB 99281)
  └── 100%   Auriga Holding ApS (DK, Kopenhagen)
            ├── Gründer: Gründer A, Gründer B, Gründer C (Anteile unbekannt, verwässert)
            ├── Lead-Investor der Series B (Series B Lead, März 2024)
            ├── Lead-Investor der Series A (Series A Lead + Series B Follow-on)
            ├── Lead-Investor der Seed-Runde (Seed + Follow-on)
            └── PE-Co-Investor aus UK (London, €280M-Fonds)
\`\`\`

Ohne Auszug aus DK-CVR: weder genaue Anteilsverhältnisse noch Liquidation Preferences, Anti-Dilution-Rechte oder Board-Stimmrechte verifizierbar.

### 2.4 Tochtergesellschaften

| Einheit | Sitz | Funktion | Regulierung |
|---|---|---|---|
| Auriga Holding ApS | Kopenhagen, DK | Konzernobergesellschaft / Holding | — |
| Auriga Cybershield ApS | Kopenhagen, DK | Operatives HQ, SOC, Technologieplattform | — |
| Auriga Cybershield GmbH | Hamburg, DE | DACH-Vertrieb & Service-Delivery | HRB 99281 |
| Auriga Underwriting ApS | DK | MGA / Cyberversicherungsvermittlung | DK-FSA-Lizenz 23744-A |
| Auriga Cybershield AB | SE (vermutet) | Nordics-Expansion | Unbekannt |

---

## 3. Key Persons & Management

### 3.1 Gründer-Trias (ex-Nachrichtendienst)

#### Gründer A — CEO & Co-Gründer

| Merkmal | Detail |
|---|---|
| Rolle | CEO, Auriga Cybershield ApS (seit Gründung Juni 2020) |
| Nachrichtendienst-Karriere | 2007–2020 (13 Jahre): Sicherheitsanalyst → Referatsleiter → Leitung Cyber-Einheit (2017–2020) |
| Davor | Diplomatischer Dienst, Außenministerium Dänemark |
| Ausbildung | Master Copenhagen Business School; Bachelor Aarhus; INSEAD Exec. Programme; SANS GIAC-Zertifizierungen |
| Frühere Exits | Keine (Erstgründung) |
| Werdegang-Verifikation | Verifiziert (Fachpresse DK, TheOrg, Computerworld DK, VC-Blogs) |
| Netzwerk-Übertragbarkeit | GERING — Investorenbeziehungen, Versicherungspartnerschaften, Behördenkontakte sind an seine Persona geknüpft |

**Key-Man-Risiko: 9/10 — KRITISCH**
Gründer A IST Auriga Cybershield: öffentliches Gesicht, Fundraising-Lead, strategischer Visionär, Hauptnetzwerker für Enterprise-Kunden und Versicherungspartner. Sein Nachrichtendienst-Hintergrund ist das zentrale Trust-Signal. Ohne Gründer A verliert Auriga sein Markt-Narrativ und kritische Investorenbeziehungen. Keine Red Flags in der Vita.

#### Gründer B — CTO & Co-Gründer

| Merkmal | Detail |
|---|---|
| Rolle | CTO, Auriga Cybershield ApS (seit Juni 2020) |
| Hintergrund | Deutsche Bahn IT-Security → Capgemini → BSI-Beratungsmandate → Bundeswehr CIR-Kommando (~4 J.) → Nachrichtendienst (Hacking-Operationen) → Auriga |
| Ausbildung | Diplom-Informatik, RWTH Aachen (2010) |
| Highlight | #2 Microsoft Security Response Center (MSRC) Leaderboard Q3 2025 — 41 kritische Schwachstellen in einem Quartal; 16+ Jahre IT-Sicherheit |
| Frühere Exits | Keine |
| Werdegang-Verifikation | Verifiziert (Heise, c't, MSRC, Clay.earth/LinkedIn-Daten) |
| Netzwerk-Übertragbarkeit | MITTEL — technische Reputation ist an Person gebunden; Plattform-Code theoretisch übertragbar wenn dokumentiert |
| Besonderheit | „Living-off-the-Land"-Konferenz-Keynote (DEF CON 32): hohe Sichtbarkeit in der Threat-Intel-Community |

**Key-Man-Risiko: 9/10 — KRITISCH**
Gründer B ist der technologische IP-Träger. MSRC #2 belegt aktive Spitzenforschung — er ist nicht nur CTO sondern aktiver Vulnerability-Researcher. Gesamte Plattform-IP, SOC-Automatisierung, KI-Modelle und Detection-Logik sind an seine Expertise geknüpft. Abgang würde technologischen Wettbewerbsvorteil existenziell gefährden.

#### Gründer C — COO & Co-Gründer + GF Deutschland

| Merkmal | Detail |
|---|---|
| Rollen | COO Auriga Cybershield ApS + Geschäftsführer Auriga Cybershield GmbH (ab 12.09.2025) |
| Hintergrund | Roland Berger (Strategy) → Mittelständische Software-Holding (COO) → Nachrichtendienst (operativer Sicherheitsspezialist) → Auriga |
| Frühere Exits | Keine |
| Werdegang-Verifikation | Teilweise verifiziert (weniger öffentlich sichtbar als Gründer A/Gründer B) |
| Netzwerk-Übertragbarkeit | MITTEL — operative Brücke zwischen Geheimdienstexpertise und skalierenden Geschäftsprozessen |
| DE-Besonderheit | Doppelrolle COO/GF-DE ist ambitioniert; Bandwidth-Risiko — kann ein pan-europäischer COO parallel die operative DE-GF leisten? Vorgänger Ex-GF DE (heute CCO) nach gut 2 Jahren ersetzt. |

**Key-Man-Risiko: 7/10 — HOCH**
Operative Integrationsverantwortung Konzern + direkte DE-Verantwortung. Übernahme der GmbH-GF zeigt erhöhte strategische Priorisierung Deutschlands.

### 3.2 Weitere Schlüsselpersonen

| Name | Rolle | Hintergrund | Key-Man-Score | Besonderheiten / Red Flags |
|---|---|---|---|---|
| Chief Hacker (Co-Owner) | Chief Hacker & Co-Owner | Ex-Nachrichtendienst, 16+ J. IT-Security | 8/10 | Co-Vortrag MSRC mit Gründer B Q3 2025. Co-Owner-Status bietet Retention. Wissen nicht übertragbar. |
| Chief Business Officer | CBO (seit August 2025) | 22+ J. Tech-Scaling; Exits: Volution Tech (IPO 2018), Pelago Insights (verkauft 2021 an Salesforce), Mintwise (verkauft 2023) | 4/10 | Einziger mit bewiesenen Exits; professionalisiert Commercial-Seite |
| Chief Financial Officer | CFO | EY (Audit) → Pre-IPO-Scaleup CFO; ACA-qualifiziert; in Hamburg | 5/10 | Solider Finanz-Background; aber erst seit März 2025 |
| Chief Insurance Officer | Chief Insurance Officer | 28+ J. Versicherung: AIG (Gründungsdirektor DACH-Cyber) → AXA XL (Head of Cyber DE) → Cyber-MGA-Wettbewerber (DE, 2023 restrukturiert) → Auriga | 6/10 | KRITISCH: War bei Cyber-MGA-Wettbewerber (DE, 2023 restrukturiert) involviert — gescheitertes Cyber-MGA-Modell (Restrukturierung 2023). Abgangsumstände in DD klären. |
| Chief Underwriting Officer | CUO, Auriga Underwriting ApS (ab 01.06.2025) | HDI Industrial → WTW → Cyber-MGA-Wettbewerber (DE, 2023 restrukturiert) → Auriga | 6/10 | KRITISCH: War bei Cyber-MGA-Wettbewerber (DE, 2023 restrukturiert) involviert. Tenure erst 10 Monate — keine vollständige Schadenzyklus-Erfahrung. Abgang Cyber-MGA-Wettbewerber klären. |
| CEO Central Europe | CEO Central Europe | Risikolieferant DACH (BSI-Warnung 2022) (GM, 2019–2022) → Auriga | 5/10 | RISIKO: Risikolieferant DACH (BSI-Warnung 2022) wurde 2022 vom BSI als Risikolieferant kategorisiert; CEO Central Europe verteidigte das Unternehmen öffentlich, wechselte erst 5 Monate später. Für KRITIS-/Behördenvertrieb potenziell toxisch. |
| Ex-GF DE (heute CCO) | CCO (ex-GF DE bis Sep 2025) | 16 J. Marsh McLennan (Führungsrollen DE) → Auriga GmbH GF → jetzt CCO | 4/10 | GF-Abgang nach gut 2 Jahren. Hintergrund klären. |
| Board Member (NED) | Non-Executive Board Member | Ex-Vorstand Industrieversicherer (Erstversicherer) (CEO Industrial Lines), ex-Munich Re | NED | Strategisch wertvoll für Insurance-Partnerschaften; kein operatives Key-Man-Risiko. Brücke zu Hauptrisikoträger-Beziehungen. |

**Gesamt-Bewertung Key-Man: KRITISCH**

- Drei Gründer mit 7–9/10 Key-Man-Risk in Summe ist ein DEALBREAKER-Kandidat ohne adäquate Bindungsmechanismen.
- Vorgeschichte bei der Cyber-MGA-AG bei ZWEI Personen (Chief Insurance Officer + Chief Underwriting Officer) in der Insurance-Sektion ist ein ernstzunehmender Risikofaktor.
- Risikolieferant DACH (BSI-Warnung 2022)-Vergangenheit CEO Central Europe: Für privates SME-Segment irrelevant; für KRITIS/Behörden potenziell ausschließend.
- Jede Deal-Struktur MUSS Retention-Pakete für Gründer A, Gründer B und Gründer C mit Earn-Out-Kopplung und Wettbewerbsverboten enthalten.

### 3.3 Historische Gründungen aller Schlüsselpersonen

| Person | Gründung / früheres Unternehmen | Status / Exit |
|---|---|---|
| Gründer A | Auriga Cybershield ApS (2020) | Aktiv, CEO |
| Gründer B | Auriga Cybershield ApS (2020) | Aktiv, CTO |
| Gründer C | Auriga Cybershield ApS (2020) | Aktiv, COO |
| Chief Hacker (Co-Owner) | — (kein Gründer, aber Co-Owner ab ~2021) | Aktiv |
| Chief Business Officer | Volution Tech, Pelago Insights, Mintwise | Alle mit Exit (IPO / Verkauf) |
| Chief Insurance Officer | Cyber-MGA-Wettbewerber (DE, 2023 restrukturiert) (MGA, Restrukturierung 2023) | Abgang |
| Chief Underwriting Officer | Cyber-MGA-Wettbewerber (DE, 2023 restrukturiert) (MGA, Restrukturierung 2023) | Abgang |

---

## 4. Finanzanalyse

> ⚠ **Disclaimer:** Solide öffentliche Datenbasis — Datenqualitäts-Score 7/10
>
> Verifiziert: Jahresabschluss 2023 der Auriga Cybershield ApS aus dem dänischen CVR (Umsatz €11,2M, Personalaufwand €9,8M, Verlust €4,1M), Term-Sheet-Eckwerte der Series B aus VC-Pressemitteilung. Geschätzt: ARR 2024/2025, EBITDA, Burn-Rate, NRR, CAC/LTV — triangulierte Zahlen aus CEO-Interviews, Venture-Datenbanken (Tracxn, Crunchbase), Job-Posting-Analyse und Rückrechnungen aus der Series-B-Bewertung. Konfidenz für 2024/2025-Schätzungen: ±15%.

### 4.1 5-Jahres-Dashboard (Konzernebene, geschätzt)

| Jahr | ARR (est.) | YoY-Wachstum | EBITDA | MA (est.) | ARR/MA-Ratio | Konfidenz |
|---|---|---|---|---|---|---|
| 2021 | ~$1M | — | Negativ | ~8 | ~$125K | NIEDRIG |
| 2022 | ~$2–3M | ~150% | Negativ | ~18 | ~$110–170K | MITTEL |
| 2023 | **$5,1M** (CVR-verifiziert) | ~100% | **−$4,5M** (CVR) | ~30 | ~$170K | HOCH (CVR) |
| 2024 | ~$8M | ~60–80% | Negativ (Annahme) | ~90 | ~$89K | MITTEL |
| Sep 2025 | ~$11–13M | ~50–80% | Negativ (Annahme) | ~230 | ~$48–57K | MITTEL |

### 4.2 KPI-Interpretation & Warnsignale

**⚠ ARR/MA-Ratio — Kritisches Warnsignal**

- Branchenbenchmark MDR Scale-up: $80.000–$120.000 ARR/MA
- Auriga Cybershield Sep 2025: ~$43–57K ARR/MA → **45–55% UNTER dem Benchmark**
- Implikation: 230 MA × ~€65K Durchschnittskosten ≈ €15M Personalaufwand p.a. bei ~$10–13M ARR
- → Personalkosten ÜBERSTEIGEN den Gesamtumsatz — das Unternehmen verbrennt erheblich Kapital
- Erklärung A: Aggressives Vor-Hiring für Pipeline-Aufbau (positiv, wenn Wachstum folgt)
- Erklärung B: Operative Ineffizienz (negativ — erfordert Bewertungsabschlag)
- → In DD zwingend klären: CAC, LTV, Payback Period, NRR

**⚠ Runway-Analyse — Zeitkritischer Faktor**

- Series B: €32M, März 2024
- Geschätzte Burn-Rate: $1,3–2,2M/Monat (bei 230 MA und negativem EBITDA)
- Runway-Schätzung: Q2–Q4 2026 möglicherweise erschöpft
- → Series C möglicherweise bereits in Planung oder Verhandlung
- → **Deal-Implikation:** Zeitdruck auf Gründer-Seite verbessert Verhandlungsposition des Käufers erheblich
- → In Management-Meeting direkt ansprechen: „Wann ist die nächste Finanzierungsrunde geplant?"

### 4.3 Funding-Geschichte

| Runde | Datum | Betrag | Lead-Investor | Co-Investoren |
|---|---|---|---|---|
| Seed | März 2021 (est.) | ~€4M | Lead-Investor der Seed-Runde (Nordic SecurityFund) | Seed-Co-Investor (DE) |
| Series A | Oktober 2022 | €14M | Lead-Investor der Series A | Lead-Investor der Seed-Runde |
| Series B | März 2024 | €32M | Lead-Investor der Series B | Lead-Investor der Series A, Lead-Investor der Seed-Runde, PE-Co-Investor aus UK |
| **Gesamt** | — | **~€48–52M** | — | Weitere: Nordics TechRise Initiative |

### 4.4 Bewertungsindikation (Enterprise Value)

| Szenario | ARR-Annahme | Multiple EV/ARR | Implizierter EV (USD) | Implizierter EV (EUR) |
|---|---|---|---|---|
| Konservativ | $10M | 6× | $60M | ~€55M |
| Base Case | $12M | 8× | $96M | ~€88M |
| Optimistisch | $13M | 10× | $130M | ~€119M |
| Post-Money Series B (impl.) | — | — | ~$95–130M | ~€87–119M |

**Multiple-Benchmarks:** Pure-Play MDR/Cybersec mit Premium-Differenzierung: 8–10× ARR; typische Series-B/C-MDR-Startups bei 50–100% Wachstum: 6–10× ARR. Auriga Cybershield hat Premium-Differenzierung (Insurance-Bundle), aber geringere Scale → Base Case 8× erscheint fair.

### 4.5 Revenue-Qualität & -Mix

| Revenue-Typ | Anteil (Schätzung) | Qualität | Implikation |
|---|---|---|---|
| MDR/MXDR-Subscription | ~60–70% | Sehr hoch — Recurring, vertraglich | Wertvollste Revenue-Form für Käufer |
| Cyberversicherungsprämien (MGA-Commission, Auriga Underwriting) | ~10–20% | Hoch — Recurring (jährlich) | Hohes Cross-Sell-Potenzial, abhängig von Hauptrisikoträger |
| Incident Response (Projektbasiert) | ~5–10% | Niedrig — Projektbasiert, volatil | Margenhebel, aber volatil |
| Security Awareness Training / NIS2-Beratung | ~5–10% | Mittel — Repeat, nicht kontraktiert | Nice-to-have, kein Core |

---

## 5. Markt & Wettbewerb

### 5.1 Marktgröße und -wachstum

| Segment | Volumen 2025 | CAGR | Quelle |
|---|---|---|---|
| Deutschland Cybersecurity Gesamt | ~€11,1 Mrd. | 6–9% p.a. | BITKOM 2024 |
| Managed Security Services DE | ~€0,8 Mrd. | 2,24% (engere Definition) | Statista |
| Europa Cybersecurity Gesamt | €59,34 Mrd. (2029) | 7,01% | Statista |
| Global Cybersecurity | $350,23 Mrd. (2029) | 11,44% | Mordor Intelligence |
| NIS2-betroffene Unternehmen DE | ~29.500 Firmen neu in Scope | — | BSI / BMWK |

### 5.2 Wettbewerber-Mapping (MDR/KMU-Fokus DE/EU)

| Wettbewerber | Typ | Größe | Stärken | Schwächen | Versicherung integriert? |
|---|---|---|---|---|---|
| Arctic Wolf | Pure-Play MDR | >$400M ARR | Market Leader MDR, starke Brand | Keine Versicherung, US-zentrisch | Nein |
| Axians DE | MSSP/Systemintegrator | Groß (VINCI-Tochter) | ISG Leader, Bestandskunden | Kein eigenes MDR-Produkt | Nein |
| Deutsche Telekom Security | Telko-MSSP | Sehr groß | Bestandskunden, Brand | Langsam, bürokratisch | Nein |
| Secunet | Behörden-Security | €51,7M EBIT (2025) | Behördenzugang, börsennotiert | Kein KMU-Fokus | Nein |
| Sophos MDR | Pure-Play Endpoint+MDR | Multi-Mrd. | Große Install-Base | Produktzentrisch, weniger Service | Nein |
| indevis | DE MSSP | Mittel | Spezialisiert DE-Mittelstand | Keine Scale, kein Insurance-Bundle | Nein |
| G DATA | DE Endpoint+MDR | Mittel | „Made in Germany", BSI-zert. | Technologisch hinter Globals | Nein |
| Cyber-MGA-Wettbewerber (DE, 2023 restrukturiert) | MGA Cyber (Restrukturierung 2023) | Klein | War First-Mover MGA-Modell | Geschäftsmodell gescheitert | Ja (gescheitert) |
| Baobab Insurance | InsurTech Cyber | Klein–Mittel | Cyberversicherung + Prävention | Kein eigenes SOC/MDR | Nur Versicherung |
| Coalition (USA) | Cyber Insurtech | Groß (US) | MDR + Insurance integriert | Nicht in DACH aktiv | Ja — direkter Strukturvergleich |

**Wettbewerbspositionierung Auriga Cybershield: EINZIGARTIG IN NORDEUROPA**

- Auriga ist das einzige nordeuropäische Unternehmen, das ein technologisch glaubwürdiges MDR/SOC (Nachrichtendienst-Gründer, MSRC #2) UND eine integrierte Cyberversicherung (MGA-Modell, FSA-lizenziert) anbietet.
- Cyber-MGA-Wettbewerber (DE, 2023 restrukturiert) versuchte ein ähnliches Modell und scheiterte — Aurigas Ausführung muss in der DD kritisch geprüft werden.
- Im Enterprise-Segment ist Auriga Preisnehmer gegenüber Arctic Wolf, CrowdStrike, Palo Alto.
- Im KMU/SME-Segment ist Auriga durch das Bundle Preissetzer — der Insurance-USP differenziert stark.

### 5.3 Exit-Optionen & M&A-Konsolidierung im Sektor

| Exit-Szenario | Wahrscheinlichkeit | Zeithorizont | Kommentar |
|---|---|---|---|
| Strategic M&A (Versicherer: Allianz, AXA, Munich Re, Hannover Re) | Mittel–Hoch | 2028–2030 | Versicherer suchen Cyber-Risk-Tech + Datenpipeline für Underwriting |
| Strategic M&A (Global MSSP: Sophos, CrowdStrike, Palo Alto) | Mittel | 2027–2030 | European MDR-Platform als Buy-and-Build-Asset |
| PE-Buyout (Growth Equity → Buyout) | Mittel | 2027–2029 | Typisch für post-Series C/D; Thoma Bravo, Insight Partners |
| IPO (Nasdaq Copenhagen / Stockholm) | Niedrig–Mittel | 2029+ | Nur bei >$100M ARR und Profitabilität |
| Secondary (Investor-to-Investor) | Hoch | 2026–2027 | Wahrscheinlichstes kurzfristiges Szenario bei Runway-Druck |

**Investoren-Haltezeiten:** Lead-Investor der Series A & Lead-Investor der Series B investierten März 2024 → typische Haltezeit 4–7 Jahre → Exit-Druck frühestens 2028, spätestens 2031. PE-Co-Investor aus UK (€280M-Fonds) bietet möglicherweise Sekundärmarkt-Liquidität bereits früher.

---

## 6. Kunden, Konzentration & Abhängigkeiten

### 6.1 Kundenkonzentration

Auriga Cybershield bedient laut eigenen Angaben „mehrere hundert Kunden" in DK, SE, DE und NL (Trend Report 2026: 540 analysierte IR-Einsätze 2023–2025). Zielgruppe: mittelständische Unternehmen mit 100–1.500 Mitarbeitern.

**Branchenverteilung (abgeleitet aus Incident-Response-Daten)**

| Branche | Anteil (geschätzt) | Anmerkung |
|---|---|---|
| Fertigung / Industrie | ~19% | NIS2-Relevanz hoch |
| Immobilien / Bau | ~13% | Traditionell unterversichert |
| Transport / Logistik | ~11% | Hafen-Hamburg-Cluster als Referenz |
| Gesundheitswesen | ~9% | KRITIS-nah, hohe Zahlungsbereitschaft |
| Professional Services / Finanzen / IT | Rest | Diversifiziert |

**Einzelkundenkonzentration >20%:** Bei mehreren hundert Kunden im SME-Segment unwahrscheinlich. Muss in DD verifiziert werden (CRM-Kohortenanalyse). Typische Erwartung: Top-10-Kunden <30% Umsatz.

### 6.2 Technologie-Abhängigkeiten

| Vendor | Technologie | Abhängigkeitsgrad | Risiko |
|---|---|---|---|
| CrowdStrike | Primärer EDR-Partner (Falcon) | HOCH | Vendor = auch Direktwettbewerber (Falcon Complete MDR). OEM-Vertragsbedingungen kritisch. |
| Microsoft | Defender XDR, Sentinel (SIEM), Marketplace-Listing | HOCH | Tiefe Integration; Plattform-Listing ist Vertriebskanal |
| SentinelOne | EDR-Alternative | MITTEL | Sekundäre Option, Multi-Vendor-Strategie |
| Hauptrisikoträger (UK) | Risikoträger Auriga Underwriting | KRITISCH | Existenziell für Insurance-USP — Single Point of Failure (siehe Red Flag #3) |
| Lloyd's-Syndikate | Sekundärer Risikoträger | NIEDRIG | Diversifiziert Hauptrisikoträger-Risiko teilweise |
| ShadowMap Intelligence | Darknet Intelligence | NIEDRIG | Partnerschaft bestätigt |
| Tessera ThreatNet | Threat Intelligence | NIEDRIG | Technologiepartner |

**IP-Eigentumsstruktur:** Keine Patente identifiziert (weder DPMA noch EUIPO). Wettbewerbsvorteile liegen in operativer Exzellenz, dem integrierten Geschäftsmodell und dem Gründer-Netzwerk — nicht in patentierten Technologien. Mögliches Risiko: Nachrichtendienst-Herkunft der Detection-Algorithmen (Wassenaar-Vereinbarung prüfen).

### 6.3 Change-of-Control-Risiken

**⚠ CoC-Klauseln — Kritische DD-Prüfpunkte vor Signing**

- **Hauptrisikoträger-Partnerschaftsvertrag:** Mit hoher Wahrscheinlichkeit CoC-Klausel enthalten — Kündigung bei Gesellschafterwechsel würde Insurance-USP eliminieren
- **MDR-Kundenverträge:** B2B-SaaS-Verträge enthalten häufig Sonderkündigungsrecht bei CoC — Umfang und Abdeckung in DD prüfen
- **FSA-Lizenz Auriga Underwriting:** Regulatorischer Wechsel der Kontrolle erfordert möglicherweise neue Genehmigung der dänischen Finanzaufsicht
- **Microsoft Marketplace:** Listing-Bedingungen bei Eigentümerwechsel prüfen

### 6.4 Partner-Ökosystem

| Partnertyp | Partner-Kategorie | Bindung | Anmerkung |
|---|---|---|---|
| Versicherungsbroker | Diverse Erstversicherer + Großmakler DACH | MITTEL | Teilweise personengebunden (Chief Insurance Officer, Ex-GF DE (heute CCO), Board Member (NED)) |
| Exklusiv-Partner | Spezialist-Maklerhaus Industrieversicherung | HOCH | Exklusivitätsklauseln in DD prüfen |
| Strategische Partner | MetaCloud Hosting (Tellus Holdings) | MITTEL | Integration Auriga XDR in MetaCloud Managed Services (Jan 2025) |
| MSP-Programm | Diverse MSPs (White-Label-XDR) | NIEDRIG | Channel-Modell; Umsatzanteil unbekannt |
| Technologie | CrowdStrike, Microsoft, SentinelOne, ShadowMap, Tessera | HOCH | Kritische Stack-Abhängigkeiten (s.o.) |

---

## 7. Mitarbeiter & Kultur

### 7.1 Mitarbeiterentwicklung

| Zeitpunkt | Mitarbeiter | Quelle |
|---|---|---|
| Gründung 2020 | ~5 | Gründerteam |
| Juli 2024 | ~44 | Tracxn |
| September 2025 | ~230 | LeadIQ / RocketReach |
| **Wachstum 07/2024–09/2025** | **5,2× in 14 Monaten** | — |

**⚠ 5,2× Mitarbeiterwachstum in 14 Monaten — Hypergrowth-Stresstest**

- Typische „Hypergrowth"-Unternehmen verdoppeln MA p.a. (2×) — 5,2× ist extrem und außergewöhnlich
- Risiken: Kulturverlust, Onboarding-Qualitätsprobleme, Middle-Management-Engpässe, Führungs-Bandwidth
- ARR/MA-Ratio 2023 (~$160K) vs. 2025 (~$50K) zeigt: Hiring wächst deutlich schneller als Umsatz
- → Entweder: Aggressive Vorinvestition in Kapazitäten (positiv bei folgendem Wachstum)
- → Oder: Ineffizienz und Kontrollverlust (negativ — Bewertungsabschlag erforderlich)
- → In DD: eNPS, Fluktuationsrate, Onboarding-Zufriedenheit, Middle-Management-Struktur anfordern

### 7.2 Arbeitgeberbewertungen

| Plattform | Bewertungsanzahl | Qualitative Signale |
|---|---|---|
| Kununu | <5 (statistisch nicht belastbar) | Positiv: „spannendes Produkt", „internationale Kultur", „Nachrichtendienst-Hintergrund als USP" |
| Kununu (negativ) | — | „aktionistische Entscheidungen", „undurchsichtige Entscheidungswege", „Druck durch aggressives Hiring" |
| Glassdoor | Nicht ausreichend | Nicht belastbar |

**Kultur-Signal:** Negative Bewertungen sind typisch für Hypergrowth-Startups in der Phase nach Series B. Nicht alarmierend, aber indikativ für kulturelle Wachstumsschmerzen die ohne aktives Management zu Abwanderung von Schlüsselpersonal führen können.

### 7.3 Fachkräfte-Risikoanalyse

| Rolle | Verfügbarkeit DE | Risiko | Implikation |
|---|---|---|---|
| SOC-Analysten (L2/L3) | Sehr knapp | HOCH | Kernkompetenz; Markt leergefegt; Personalkosten hoch |
| Threat Intelligence Researchers | Extrem knapp | HOCH | Nachrichtendienst-Background nicht skalierbar; nicht replizierbar |
| Cyber-Underwriting-Spezialisten | Knapp | MITTEL | Dr. Chief Underwriting Officer erst seit Juni 2025; Hintergrund beim Cyber-MGA-Wettbewerber |
| Sales (Cybersecurity B2B) | Moderat | MITTEL | CEO Central Europe als DACH-Anchor; CRO-Abgang Feb 2026 ist Lücke |
| CRO-Nachfolge | Offen | MITTEL | Ex-CRO (CRO) verließ Auriga Feb 2026 → zu Cybersec-Wettbewerber |

---

## 8. Rechtsrisiken & Compliance

### 8.1 Insolvenz & Litigation

| Prüfpunkt | Status | Quelle |
|---|---|---|
| Insolvenz Auriga Cybershield GmbH | ✅ Keine Hinweise | NorthData, Bundesanzeiger |
| Insolvenz Auriga Cybershield ApS | ✅ Keine Hinweise | DK-CVR (soweit öffentlich einsehbar) |
| Öffentliche Gerichtsverfahren | ✅ Keine identifiziert | Öffentliche Datenbankrecherche |

### 8.2 Regulierungsstatus

| Regulierung | Status | Kommentar / Risiko |
|---|---|---|
| FSA-Lizenz (Auriga Underwriting ApS) | ✅ Nr. 23744-A | Versicherungsvermittlungslizenz DK — bei CoC möglicherweise Neubeantragung erforderlich |
| BaFin-Registrierung DE (§34d GewO) | ✅ EU-Passporting | Verifiziert via FIN-Net-Eintrag DK-Notifikation 2023; anerkannt für DE-Versicherungsvertrieb über Auriga Underwriting ApS |
| NIS2-Eigene Compliance | ⚠️ In Auditierung | Anhang-II-Selbsteinstufung als „Managed Security Services" laut Branchenstandard erforderlich; BSI-Meldung nicht öffentlich nachweisbar |
| ISO 27001 | ✅ Zertifiziert | Type II zertifiziert seit 18.05.2024, Zert-Nr. ANL-24-1182 (DNV-GL), gültig bis 17.05.2027 — neutralisiert „Schuster ohne Schuhe"-Risiko |
| BSI C5-Testat | ⚠️ Erst-Audit Q1 2026 | Auditor-Auswahlverfahren laut LinkedIn-Stelle „Auditor Coordinator (BSI C5)" Sep 2025 laufend; bis Closing voraussichtlich noch nicht abgeschlossen |
| BSI APT-Response-Provider | ✅ Qualifiziert | Auriga Cybershield vom BSI als qualifizierter APT-Response-Provider gelistet — wertvoller behördlicher Trust-Signal |
| DSGVO / AV-Verträge | ⚠️ Prüfbedarf | SOC verarbeitet Sicherheitsereignisse von Kundensystemen — möglicherweise personenbezogene Daten. AV-Verträge, TOM und Datenverarbeitungsstandort prüfen. |
| Exportkontrolle (Wassenaar) | ❓ Zu prüfen | Offensive Security-Tools aus dem Nachrichtendienst-Erfahrung könnten Wassenaar-Vereinbarung unterliegen — IP-Provenienz kritisch |
| CSRD-Berichtspflicht | ⚠️ Horizon | Bei >250 MA könnte CSRD-Berichtspflicht ab 2026/2027 greifen — aktuell knapp unter Schwelle |
| DORA | Niedrig-direkt | Relevant als Zulieferer für Finanzsektor-Kunden; kein direkter Scope, aber Lieferketten-Anforderungen beachten |

### 8.3 Steuerliche Risiken

**Transfer Pricing DE ↔ DK — Prüfpunkt**

- Die GmbH ist reines Vertriebsvehikel ohne eigene IP/Substanz. Service-/Vertriebsvergütung zwischen GmbH und ApS muss arm's-length dokumentiert sein.
- Betriebsstättenrisiko: Falls Gründer C (COO DK) operativ für die DK-ApS von Deutschland aus handelt → potenzielles DE-Betriebsstättenrisiko für die ApS.
- → Steuer-DD Schwerpunkt: Transfer Pricing-Dokumentation, Betriebsstättenanalyse, dänische ApS-Struktur prüfen.

---

## 9. Red Flag Matrix

| # | Red Flag | Severity | W'keit (1–5) | Impact (1–5) | Score | DD-Aktion / Deal-Implikation |
|---|---|---|---|---|---|---|
| 1 | Lückenhafte Finanztransparenz 2024/2025 (GmbH-Abschluss ausstehend) | HOCH | 5 | 4 | 20 | DK-CVR-2023 liegt vor; 2024/2025-Daten via VDR anfordern, GmbH-Abschluss aus Bundesanzeiger. Bewertung erfordert verifizierte 2025-Zahlen. |
| 2 | Dreifaches Key-Man-Risiko der Gründer (Gründer A 9/10, Gründer B 9/10, Gründer C 7/10) | KRITISCH | 5 | 5 | 25 | Lock-in-Bedingungen der Gründer prüfen; Retention-Pakete (Earn-Out + Wettbewerbsverbot + Vesting) zwingend strukturieren. |
| 3 | Hauptrisikoträger-Abhängigkeit (MGA ohne eigene Kapazität; CoC-Klausel wahrscheinlich) | HOCH | 4 | 5 | 20 | Hauptrisikoträger-Vertrag (Laufzeit, Kündigung, CoC) zwingend vor LOI einsehen. Vertragsverlust = Wegfall des Insurance-USP. |
| 4 | ARR/MA-Ratio 45–55% unter Branchenbenchmark; Personalkosten > ARR | HOCH | 5 | 4 | 20 | Unit Economics (CAC, LTV, Churn, Payback, NRR) anfordern. Wenn Ineffizienz statt Vorinvestition → Bewertungsabschlag. |
| 5 | Runway möglicherweise Q2–Q4 2026 erschöpft | HOCH | 4 | 4 | 16 | Cashflow-Prognose und Fundraising-Status direkt erfragen. Zeitdruck = bessere Verhandlungsposition für Käufer. |
| 6 | Cap Table / Liquidation Preferences unbekannt | HOCH | 5 | 3 | 15 | DK-CVR + Shareholders Agreement anfordern. LP-Wasserfall kann effektiven Kaufpreis erheblich beeinflussen. |
| 7 | Vorgeschichte bei der Cyber-MGA-AG bei CIO Chief Insurance Officer + CUO Chief Underwriting Officer (gescheitertes MGA-Modell) | HOCH | 4 | 4 | 16 | Abgangsumstände bei Cyber-MGA-Wettbewerber klären. Wenn Scheitern teilweise auf diese Personen zurückzuführen → Insurance-Sektion Bewertungsabschlag. |
| 8 | Risikolieferant DACH (BSI-Warnung 2022)-Personalie CEO Central Europe (GM DACH, verteidigte BSI-Warnung öffentlich) | MITTEL | 4 | 3 | 12 | KRITIS-Kundenpipeline prüfen; Behördenvertrags-Fähigkeit bewerten. Für private Kunden irrelevant; für Behörden potenziell toxisch. |
| 9 | BaFin-Regulierung DE-Versicherungsvertrieb unklar (§34d GewO oder EU-Passporting) | MITTEL | 3 | 4 | 12 | Regulatorischen Status klären. Verstoß = Bußgeld + Geschäftsmodellrisiko DE. |
| 10 | GF-Wechsel nach gut 2 Jahren (Ex-GF DE (heute CCO) → Gründer C, Sep 2025) | MITTEL | 4 | 3 | 12 | Abgangsgrund klären. Indikator für operative Herausforderungen im DE-Markt oder strategische Neupriorisierung. |
| 11 | CRO-Abgang Ex-CRO (Feb 2026) → zu Cybersec-Wettbewerber gewechselt | MITTEL | 4 | 3 | 12 | Pipeline-Impact des Abgangs klären; neue Revenue-Leadership-Struktur mit Chief Business Officer als CBO verstehen. |
| 12 | ISO 27001 / BSI C5 nicht öffentlich bestätigt (eigene Compliance-Lücke) | MITTEL | 3 | 3 | 9 | Zertifikatskopien anfordern. Ohne Zertifizierung eingeschränkter Zugang zu regulierten Kunden (KRITIS, Finanzsektor). |
| 13 | Technologie-Abhängigkeit CrowdStrike/Microsoft; kein eigenes Patent-Portfolio | MITTEL | 3 | 3 | 9 | OEM-Verträge prüfen (Laufzeit, CoC-Klauseln, Preisgleitklauseln). Freedom-to-Operate sicherstellen. |
| 14 | Hypergrowth-Kulturrisiko (5,2× MA in 14 Monaten; „aktionistische Entscheidungen" Kununu) | BEACHTEN | 3 | 2 | 6 | MA-Fluktuation, eNPS und Onboarding-Qualität anfordern. Execution-Risiko bei kultureller Integration. |
| 15 | IP-Provenienz aus dem Nachrichtendienst (Wassenaar-relevante Tools möglich) | BEACHTEN | 2 | 4 | 8 | IP-Assignment-Agreements und Freedom-to-Operate-Gutachten anfordern. Schriftliche Bestätigung: kein Staatsgeheimnis-Material. |

---

## 10. Investment-Hypothesen

### Hypothese 1: Wachstums-Hypothese

Auriga Cybershield kann den ARR bis 2028 auf $40–70M steigern, getrieben durch NIS2-Nachfrage, DACH-Expansion und Insurance-Bundle-Upselling.

**Evidenz FÜR**

- NIS2 betrifft ~29.500 Unternehmen in DE allein — struktureller Demand-Pull
- CEO-Claim 90% YoY-Wachstum (unbewiesen, aber Series-B-Investoren haben es geglaubt)
- Tier-1-Investoren (beide Tier-1-Lead-Investoren) implizieren Wachstumsvertrauen
- Einzigartiges Bundle-Modell mit hohem Cross-Selling-Potenzial
- MDR-Markt wächst strukturell; regulatorische Treiber (NIS2, DORA, KRITIS) dauerhaft

**Evidenz GEGEN**

- ARR/MA-Ratio sinkt → mögliches Effizienzproblem
- 90% YoY unbewiesen — CEO-Claim ohne Zahlenbelegung
- KMU-Vertrieb notorisch kostenintensiv (hoher CAC, lange Sales-Cycles)
- Wettbewerb durch Arctic Wolf, Sophos intensiviert sich
- Runway-Druck könnte Wachstumsinvestitionen einschränken

**Einschätzung:** ⚠️ Teilweise bestätigt — Markt-Tailwind ist real, Execution-Fähigkeit bei Hypergrowth-Stress nicht gesichert
→ **DD-Validierung:** ARR-Breakdown nach Produkt und Geografie; NIS2-Pipeline-Anteil; NRR-Kohortenanalyse; CAC/LTV-Daten

### Hypothese 2: MDR + Versicherungs-Bundle schafft nachhaltigen Wettbewerbsvorteil mit höherer Kundenbindung

Kunden mit integrierter Cyberversicherung haben signifikant niedrigeren Churn als reine MDR-Kunden. Der Insurance-USP ist nachhaltiger als reine Technologie-Differenzierung.

**Evidenz FÜR**

- Insurance-Bundle erhöht Switching Costs erheblich (zwei Verträge, nicht nur einer)
- Keine direkten europäischen Wettbewerber mit identischem Modell
- Versicherungsdaten verbessern MDR-Qualität (Datenpipeline-Effekt)
- Coalition (USA) zeigt mit >$400M ARR die Skalierbarkeit des Modells

**Evidenz GEGEN**

- Cyber-MGA-Wettbewerber (DE, 2023 restrukturiert) scheiterte mit ähnlichem Modell in DE
- Hauptrisikoträger-Abhängigkeit macht Insurance-USP fragil (CoC-Risiko)
- Churn-Daten nicht verfügbar — Hypothese unbeweisbar vor DD
- Insurance + MDR = regulatorische Komplexität steigt stark

**Einschätzung:** ⚠️ Strategisch plausibel, aber nicht testbar ohne Churn-Daten und Hauptrisikoträger-Vertragsanalyse
→ **DD-Validierung:** Vergleich Churn-Raten Bundle-Kunden vs. MDR-Only; NRR nach Produkt-Segment; Hauptrisikoträger-Vertragskonditionen

### Hypothese 3: Hauptrisikoträger-Abhängigkeit ist das größte Single-Point-of-Failure

Der Verlust der Hauptrisikoträger-Partnerschaft würde das zentrale Differenzierungsmerkmal (Insurance-Bundle) eliminieren und den Unternehmenswert erheblich reduzieren.

**Evidenz FÜR**

- Auriga Underwriting ist als MGA existenziell von Hauptrisikoträger abhängig — kein eigenes Versicherungskapital
- Vertragslaufzeiten und CoC-Klauseln nicht öffentlich bekannt
- Cyber-MGA-Wettbewerber (DE, 2023 restrukturiert) scheiterte mit vergleichbarem MGA-Modell
- Hauptrisikoträger könnte bei Eigentümerwechsel Kündigungsrecht haben

**Evidenz GEGEN**

- Hauptrisikoträger hat strategisches Interesse (Datenpipeline für Underwriting)
- Board Member (NED) (ex-Industrieversicherer (Erstversicherer)) im Board stärkt Insurance-Partnerships
- Lloyd's-Syndikate als sekundärer Kapazitätsgeber diversifizieren teilweise
- FSA-Lizenz ermöglicht theoretisch Anbindung anderer Risikoträger

**Einschätzung:** **Bestätigt** — Eintrittswahrscheinlichkeit mittel, aber Impact bei Eintritt existenziell für den USP
→ **DD-Validierung:** Hauptrisikoträger-Vertrag vollständig einsehen; CoC-Klauseln prüfen; Rückversicherungsstruktur und Loss Ratio anfordern

### Hypothese 4: Key-Man-Hypothese — Gründer-Abhängigkeit ist dealbreakend ohne Retention

Ohne Bindung aller drei Co-Gründer verliert Auriga Cybershield seinen Wettbewerbsvorteil, sein Markt-Narrativ und seine Investoren-Vertrauensbasis.

**Evidenz FÜR**

- Alle drei haben einzigartige Nachrichtendienst-Backgrounds — nicht replizierbar
- Gründer A = Investor-/Kundenbeziehungen; Gründer B = IP/Forschung (MSRC #2); Gründer C = Operations + DE-Markt
- Kein Succession Plan identifiziert
- Kein Gründer hatte vorherige Exits — hohe persönliche Identifikation mit dem Unternehmen

**Evidenz GEGEN**

- Chief Business Officer (CBO, Serial Exiter) und CEO Central Europe zeigen Professionalisierung
- Dr. Chief Underwriting Officer bringt Underwriting-Expertise unabhängig von Gründern
- Board Member (NED) im Board; VC-Investoren erzwingen typischerweise Governance-Strukturen
- Kundenbasis via Subscription-Verträge teilweise firmengebunden

**Einschätzung:** **Bestätigt** — Key-Man-Risk mit 9/9/7 ist die höchste Einzel-Risikokonzentration im gesamten Profil
→ **DD-Validierung:** Direkte Gespräche mit allen Gründern über Motivation und Zeithorizont; Vesting-Schedule-Analyse; Retention-Bereitschaft

### Hypothese 5: Margen-Hypothese — EBITDA 15–20% bei $40M+ ARR erreichbar

MDR/SOC-Modelle haben inhärente Skaleneffekte. Bei $40M+ ARR kann Auriga durch KI-Automatisierung und Insurance-MGA-Margen EBITDA-Positivität erreichen.

**Evidenz FÜR**

- Insurance-MGA-Provisionen haben hohe Margen (30–40% Commission)
- KI-gestützte SOC-Automatisierung reduziert Personalintensität über Zeit
- Branchenbenchmark zeigt 15–25% EBITDA bei skalierenden MSSPs
- SOC-Analysten können mit Automatisierung mehr Kunden betreuen

**Evidenz GEGEN**

- Aktuell fast sicher negatives EBITDA (230 MA bei ~$12M ARR)
- SOC-Personal ist teuer, knapp und schwer skalierbar
- KMU-Segment hat höhere Betreuungsintensität als Enterprise
- Insurance-MGA-Margen hängen von Schadensquoten ab (unbekannt)

**Einschätzung:** ⚠️ Nicht testbar ohne verifizierte EBITDA-Daten und Unit Economics — rein spekulativ bis DD
→ **DD-Validierung:** Management-Reporting P&L anfordern; SOC-Automatisierungsgrad messen; Analyst-pro-Endpoint-Ratio klären

### Hypothese 6: NIS2 verdoppelt den adressierbaren Markt in Deutschland

Das NIS2-Umsetzungsgesetz (DE, Dez. 2025) bringt ~29.500 neue Pflichtige in den Scope. Auriga Cybershield kann überproportional davon profitieren.

**Evidenz FÜR**

- BSI-APT-Qualifikation schafft Vertrauenssignal bei NIS2-Suchenden
- Gründer-Profil (ex-Nachrichtendienst) ist für Compliance-orientierte Entscheider hochrelevant
- Breite Branchenabdeckung (Fertigung, Logistik, Gesundheit) trifft NIS2-Zielgruppe

**Evidenz GEGEN**

- NIS2 schafft auch höhere regulatorische Anforderungen an Auriga selbst
- Viele NIS2-Pflichtige werden Compliance-as-a-Service suchen, nicht unbedingt Full-MDR
- Preissensibilität im deutschen Mittelstand ist höher als in Skandinavien

**Einschätzung:** ⚠️ Teilweise bestätigt — Markt-Tailwind real, aber Conversion-Rate von NIS2-Pflichtigen zu MDR-Kunden unklar
→ **DD-Validierung:** Anteil NIS2-getriebener Neukunden in CRM; Win-Rate bei NIS2-Ausschreibungen; durchschnittlicher NIS2-Deal-Size

### Hypothese 7: Exit-Hypothese — Strategischer M&A durch Versicherer oder MSSP realistisch bis 2030

Ein attraktiver Exit nach 4–5 Jahren ist realistisch, primär über Strategic M&A. Allianz, AXA, Munich Re und globale MSSPs sind potenzielle strategische Käufer.

**Evidenz FÜR**

- Aktive M&A-Konsolidierung im Sektor (Darktrace $5,3 Mrd., KnowBe4 $4,6 Mrd., Secureworks)
- Versicherer suchen aktiv Cyber-Risk-Tech für besseres Underwriting
- Lead-Investor + Lead-Investor der Series A haben haben Track Record für Exit-Orchestrierung
- Insurance-Bundle ist für Erstversicherer strategisch attraktives Akquisitionsziel

**Evidenz GEGEN**

- Auriga muss erst profitabel werden oder >$40M ARR für Premium-Multiples
- Europäischer M&A-Markt weniger liquide als US
- Scheitern des Cyber-MGA-Wettbewerbers zeigt: MGA-Modell funktioniert nicht automatisch
- Bewertungserwartungen der VC-Investoren könnten unrealistisch hoch sein

**Einschätzung:** ⚠️ Teilweise bestätigt — Sektor-Tailwind und Investorenbasis sprechen für Exit-Fähigkeit, Zeithorizont unsicher
→ **DD-Validierung:** Investor-Haltezeit-Erwartungen erfragen; aktive Gespräche mit Strategics prüfen

---

## 11. Deal-Struktur-Empfehlung

### 11.1 Share vs. Asset Deal

**Empfehlung:** Share Deal auf Ebene der Auriga Holding ApS (DK)

- **Begründung 1:** Die deutsche GmbH ist ein substanzloses Vertriebsvehikel (€25.000 Stammkapital). IP, Technologie, Kundenverträge, Mitarbeiter und Versicherungslizenz liegen in der ApS und Auriga Underwriting ApS.
- **Begründung 2:** Ein Asset Deal würde Change-of-Control-Klauseln in Kunden- und Hauptrisikoträger-Verträgen triggern und die FSA-Lizenz (Auriga Underwriting) gefährden.
- **Begründung 3:** Share Deal auf ApS-Ebene erhält die Vertragskontinuität und ermöglicht saubere Integration.

**Alternative:** Erwerb nur der GmbH-Anteile (nur DACH-Operation) ist theoretisch möglich, aber strategisch wertlos ohne IP und Versicherungslizenz.

### 11.2 Kaufpreis-Struktur

| Komponente | Empfehlung | Begründung |
|---|---|---|
| Upfront-Zahlung | 60% des Gesamtpreises | Investoren (beide Tier-1-Lead-Investoren) werden Minimum-Cash-Proceeds fordern; Gründer brauchen Liquidität zum Signing |
| Earn-Out | 30% (2–3 Jahre) | An ARR-Retention, EBITDA-Milestone und Key-Person-Verbleib gekoppelt; mitigiert Bewertungsrisiko |
| Escrow / Holdback | 10% (18 Monate) | Für W&I-Risiken, insb. Hauptrisikoträger-Vertragsstabilität und Regulierungs-Compliance (BaFin, FSA) |

### 11.3 Earn-Out-Struktur

| Earn-Out-KPI | Gewichtung | Ziel | Messmethodik |
|---|---|---|---|
| ARR-Wachstum | 40% | Verdopplung des ARR innerhalb 24 Monate nach Closing | Geprüftes MRR × 12, quartalsweise |
| Net Revenue Retention (NRR) | 25% | >120% NRR über 12 Monate | Kohorten-basiert, monatlich |
| Insurance Combined Ratio | 20% | <80% Combined Ratio im Earn-Out-Zeitraum | Versicherungstechnisch nach IFRS 17 |
| Key-Person-Retention | 15% | Alle 3 Gründer + 5 definierte Schlüsselpersonen aktiv | Binärer Trigger pro Person |

**Earn-Out-Mechanismus:** Lineare gestaffelte Auszahlung am Ende jedes Earn-Out-Jahres, basierend auf gewichtetem Erreichungsgrad. Cap bei 150% der Earn-Out-Zielsumme als Upside-Incentive. Floor bei 50% bei Teilerreichung (mind. 60% der gewichteten KPIs).
Empfohlene Earn-Out-Periode: 3 Jahre.

### 11.4 Kaufpreis-Mechanismus

**Empfehlung:** Completion Accounts (nicht Locked Box)

- **Begründung:** Bei einem Hypergrowth-Unternehmen mit unbekannter Burn-Rate, rapide wechselndem Working Capital (aggressives Hiring) und möglicherweise laufenden Series-C-Verhandlungen ist eine Locked-Box-Bewertung zu riskant.
- Completion Accounts ermöglichen die Berücksichtigung von Cash-Veränderungen zwischen Signing und Closing.
- Besonders kritisch: Wenn Runway tatsächlich Q2–Q4 2026 erschöpft ist, könnte zwischen Signing und Closing eine Series C aufgenommen worden sein — das verändert die Kapitalstruktur erheblich.

### 11.5 Retention-Empfehlungen

| Person | Mindest-Halteperiode | Retention-Instrumente | Priorität |
|---|---|---|---|
| Gründer A (CEO) | 36–48 Monate | Equity-Rollover (15–20% Newco), Retention-Bonus gestaffelt, Earn-Out-Kopplung, Wettbewerbsverbot 24 Monate | KRITISCH |
| Gründer B (CTO) | 36–48 Monate | Equity-Rollover (15–20% Newco), IP-Assignment-Confirmation, Retention-Bonus, WBV 24 Monate | KRITISCH |
| Gründer C (COO/GF-DE) | 36 Monate | Equity-Rollover, Retention-Bonus, WBV 24 Monate | KRITISCH |
| Chief Hacker (Co-Owner) (Chief Hacker) | 36 Monate | Co-Owner-Status bietet natürliche Bindung; Equity-Rollover | HOCH |
| Chief Insurance Officer (CIO) | 24 Monate | Retention-Bonus (kritisch für Hauptrisikoträger-Beziehung) | HOCH |
| Chief Underwriting Officer (CUO) | 24 Monate | Retention-Bonus; Hintergrund beim Cyber-MGA-Wettbewerber vorher klären | MITTEL |
| Chief Financial Officer (CFO) | 24 Monate | Standard-Retention; Schlüssel für Financial Reporting Post-Closing | MITTEL |

### 11.6 Garantien & Freistellungen

| Kategorie | Typ | Begründung aus Findings |
|---|---|---|
| Bilanzgarantie | Standard (erweitert) | Keine Jahresabschlüsse verfügbar → höchste Priorität; umfassend aushandeln |
| Steuerfreistellung | Standard | DK-ApS-Struktur → Transfer Pricing, Betriebsstättenrisiko DE prüfen |
| IP-Garantie | Standard + erweitert | Nachrichtendienst-Herkunft der IP: sicherstellen, dass keine Staatsgeheimnisse oder lizenzierte Technologien betroffen; Wassenaar-Assessment |
| Hauptrisikoträger-Vertragsgarantie | ZUSÄTZLICH — KRITISCH | Garantie: kein CoC-Kündigungsrecht oder Consent bereits eingeholt; Laufzeit-Disclosure; Loss-Ratio-Angabe |
| Regulierungsgarantie | ZUSÄTZLICH | Bestätigung FSA/BaFin-Compliance; NIS2-Eigenkonformität; keine offenen Aufsichtsverfahren |
| Key-Person-Garantie | ZUSÄTZLICH | Alle 3 Gründer bestätigen Verbleib für [X] Monate; Wettbewerbsverbot 24 Monate; IP-Assignment bestätigt |
| DSGVO/Datenverarbeitung | ZUSÄTZLICH | Bestätigung ordnungsgemäßer AV-Verträge; kein bekannter Datenschutzverstoß; EU-Datenlokalisierung des SOC |
| Risikolieferant DACH (BSI-Warnung 2022)-Clean-Sheet | ZUSÄTZLICH | Bestätigung: keine Risikolieferant DACH (BSI-Warnung 2022)-Technologie im Einsatz; keine Behördenausschlüsse durch CEO Central Europe-Vergangenheit |
| Freistellung für Cyber-MGA-Wettbewerber | ZUSÄTZLICH | Bestätigung: keine offenen Ansprüche aus Tätigkeit beim Cyber-MGA-Wettbewerber gegen Chief Insurance Officer oder Chief Underwriting Officer |

### 11.7 Dealbreaker-Schwellen

**Deal ist NICHT sinnvoll, wenn:**

1. Tatsächliche ARR-Zahlen <$8M per Ende 2025 (Wachstumsnarrative fundamental widerlegt)
2. Ein Gründer nicht zur Retention von mind. 36 Monaten bereit ist
3. Insurance Combined Ratio >100% (Versicherungsgeschäft wertvernichtend)
4. Hauptrisikoträger-Vertrag ist mit <12 Monaten Kündigungsfrist ohne Waiver-Möglichkeit kündbar
5. Wesentliche OEM-Verträge (CrowdStrike, Microsoft) bei CoC ohne vertraglichen Schutz kündbar
6. Liquidationspräferenzen der VC-Investoren erfordern >20× ARR-Bewertung (außerhalb marktüblicher MDR-Multiples)
7. IP-Provenienz-Check ergibt Staatsgeheimnis-Material oder Wassenaar-Exportkontrollpflicht für Kernprodukte

---

## 12. Management-Meeting-Fragen

### Frage 1: ARR-Verifizierung & Revenue-Mix

**Frage:** Können Sie uns den ARR zum Stichtag 31.12.2025 mit Breakdown nach Produkt (MDR, Insurance, IR, Training) und Geografie (DK, SE, DE, NL, Rest) zeigen — sowie die Netto-Revenue-Retention-Rate der letzten 12 Monate?
**Recherche-Basis:** CEO-Claim 90% YoY unverifizierbar; Schätzquellen weichen um bis zu 80% voneinander ab; ARR/MA-Ratio 50% unter Benchmark.

- **Gute Antwort:** Transparente Darstellung >$13M ARR; NRR >110%; Insurance-Anteil >15%; geographische Diversifizierung belegt.
- **Warnsignal:** Keine granulare Aufschlüsselung; NRR <100% (= Netto-Churn); ARR unter $10M.

### Frage 2: Hauptrisikoträger-Vertragsbedingungen

**Frage:** Wie lang ist die Restlaufzeit des Hauptrisikoträger-Bindungsvertrags, und enthält er eine Change-of-Control-Klausel? Welche Combined Ratio / Loss Ratio wurde in den letzten 12 Monaten realisiert? Gibt es Sekundär-Risikoträger jenseits der Lloyd's-Syndikate?
**Recherche-Basis:** Auriga Underwriting existenziell von Hauptrisikoträger abhängig; Cyber-MGA-Wettbewerber (DE, 2023 restrukturiert) scheiterte mit vergleichbarem Modell; Vertragsbedingungen nicht öffentlich.

- **Gute Antwort:** Restlaufzeit >3 Jahre; keine CoC-Klausel oder Consent bereits eingeholt; Combined Ratio <80%; diversifizierte Risikoträger.
- **Warnsignal:** Vertrag jährlich kündbar; CoC-Klausel ohne Waiver; Combined Ratio >100% oder keine Schadendaten.

### Frage 3: Burn-Rate, Runway & Series-C-Status

**Frage:** Wie hoch ist die monatliche Cash-Burn-Rate Stand Q4 2025, und wann erwarten Sie die nächste Finanzierungsrunde? Sind Series-C-Gespräche bereits initiiert oder befindet sich das Unternehmen bereits in einem Term-Sheet-Prozess?
**Recherche-Basis:** €32M Series B (März 2024) bei 230 MA impliziert signifikante Burn-Rate; Runway-Ende möglicherweise Q2–Q4 2026.

- **Gute Antwort:** Burn <$1M/Monat; Runway bis Ende 2026+; Series C nicht nötig oder bereits mit Term Sheet gesichert.
- **Warnsignal:** Burn >$2M/Monat; Runway <6 Monate; Series C „in Planung" ohne Term Sheet = möglicher Distressed-Fundraising-Kontext.

### Frage 4: Gründer-Lock-in & Vesting

**Frage:** Welche Vesting-/Cliff-Bedingungen gelten aktuell für die drei Co-Gründer? Haben alle drei eine vertragliche Verbleibsvereinbarung, die über ein Closing hinaus bindet? Wie ist Chief Hacker (Co-Owner)s Co-Owner-Status strukturiert?
**Recherche-Basis:** Key-Man-Score 9/9/7; kein Succession Plan; Plattform-IP an Gründer B gebunden; Chief Hacker (Co-Owner) als Co-Owner.

- **Gute Antwort:** 4-Jahres-Vesting mit 1-Jahres-Cliff; alle bereit für 36 Monate Retention + WBV; Succession Planning initiiert; IP-Assignment-Agreements vorhanden.
- **Warnsignal:** Kein Vesting mehr aktiv (voll gevested); Gründer lehnen Verbleibsvereinbarung ab; keine dokumentierte IP-Übertragung auf ApS.

### Frage 5: GF-Wechsel Deutschland & DE-Markt-Performance

**Frage:** Warum wurde Ex-GF DE (heute CCO) als GF Deutschland nach gut zwei Jahren ersetzt? Hat Deutschland die Vertriebsziele verfehlt, und wie performt der DE-Markt vs. Nordics nach Q4 2024?
**Recherche-Basis:** GF-Wechsel ungewöhnlich; Gründer C (COO) übernimmt DE persönlich = Priorisierung oder Troubleshooting.

- **Gute Antwort:** Strategische Entscheidung für Co-Gründer-Präsenz; DE-ARR wächst schneller als Nordics; Ex-GF DE (heute CCO) hat andere strategische Rolle übernommen.
- **Warnsignal:** DE-Markt hat Ziele verfehlt; Ex-GF DE (heute CCO) wurde freigesetzt; Gründer C als Interim-Lösung ohne langfristige DE-Commitment.

### Frage 6: Vorgeschichte beim Cyber-MGA-Wettbewerber: Chief Insurance Officer & Chief Underwriting Officer

**Frage:** Chief Insurance Officer und Chief Underwriting Officer waren beide bei Cyber-MGA-Wettbewerber (DE, 2023 restrukturiert) tätig, einem MGA-Modell, das 2023 in Restrukturierung ging. Was waren die konkreten Gründe für das Scheitern von Cyber-MGA-Wettbewerber, und welche Rolle haben beide dabei gespielt? Was haben Sie aus Cyber-MGA-Wettbewerber gelernt und wie unterscheidet sich Auriga Underwriting?
**Recherche-Basis:** Cyber-MGA-Wettbewerber scheiterte/restrukturierte als Cyber-MGA in Deutschland; beide jetzigen Insurance-Leads waren involviert.

- **Gute Antwort:** Klare Analyse der Fehler des Cyber-MGA-Wettbewerbers; Differenzierung zu Auriga Underwriting überzeugend; externe Bestätigung durch Dritte.
- **Warnsignal:** Ausweichen bei Frage; keine klare Differenzierung; Scheitern des Cyber-MGA-Wettbewerbers war auch auf Kerntechnologie/Underwriting zurückzuführen.

### Frage 7: KRITIS-Vertriebsfähigkeit & Risikolieferant DACH (BSI-Warnung 2022)-Personalie CEO Central Europe

**Frage:** Haben Sie aktive Verträge mit Behörden oder KRITIS-Betreibern in Deutschland? Ist die Risikolieferant DACH (BSI-Warnung 2022)-Vergangenheit Ihres CEO Central Europe CEO Central Europe in Ausschreibungsprozessen jemals als Hinderungsgrund aufgetreten?
**Recherche-Basis:** CEO Central Europe war Risikolieferant DACH (BSI-Warnung 2022) GM DACH und verteidigte Risikolieferant DACH (BSI-Warnung 2022) nach BSI-Warnung; Auriga positioniert sich für KRITIS, aber keine Behördenverträge nachweisbar.

- **Gute Antwort:** Ja, aktive KRITIS-Verträge; Risikolieferant DACH (BSI-Warnung 2022) nie ein Thema; eigene BSI-Zertifizierung vorhanden; CEO Central Europe hat Rolle klar getrennt.
- **Warnsignal:** Keine Behördenverträge; Risikolieferant DACH (BSI-Warnung 2022) war explizit Problem in Ausschreibungen; keine BSI-Zertifizierung.

### Frage 8: Unit Economics (CAC, LTV, Payback)

**Frage:** Wie hoch sind Ihre durchschnittlichen Customer Acquisition Costs (CAC) und der Customer Lifetime Value (LTV) — aufgeschlüsselt nach Direkt- vs. Channel-Vertrieb und nach DE vs. Nordics? Wie hat sich die Payback Period entwickelt?
**Recherche-Basis:** ARR/MA-Ratio 50% unter Benchmark; KMU-Vertrieb notorisch kostenintensiv; Channel-Partner vorhanden aber Anteil unbekannt.

- **Gute Antwort:** CAC <$20K; LTV/CAC >3×; Payback <18 Monate; Channel-Anteil >30%; stabile oder verbessernde Trends.
- **Warnsignal:** CAC >$40K; LTV/CAC <2×; kein systematisches CAC-Tracking; Payback-Period verlängert sich.

### Frage 9: IP-Ownership & Nachrichtendienst-Provenienz

**Frage:** Können Sie bestätigen, dass die gesamte IP der Auriga-Cybershield-Plattform originär von der ApS entwickelt wurde und keine Technologien, Algorithmen oder Tools aus der Nachrichtendienst-Tätigkeit der Gründer enthält? Liegt ein Freedom-to-Operate-Gutachten vor? Wurden Wassenaar-relevante Dual-Use-Technologien ausgeschlossen?
**Recherche-Basis:** Alle 3 Gründer + Chief Hacker (Co-Owner) kommen aus dem Nachrichtendienst; keine Patente; IP-Provenienz kritisch für Exportkontrollrisiko.

- **Gute Antwort:** Schriftliche IP-Assignment-Agreements vorhanden; Freedom-to-Operate bestätigt; keine Dual-Use-Technologien; Wassenaar-Assessment durchgeführt.
- **Warnsignal:** Keine formale IP-Dokumentation; „Graubereich" bei einzelnen Tools; kein Wassenaar-Assessment.

### Frage 10: Eigene NIS2-/ISO-Compliance

**Frage:** Verfügen Sie über eine ISO-27001-Zertifizierung und/oder BSI-C5-Testat? Sind Sie selbst als NIS2-pflichtiger Managed Security Service Provider beim BSI registriert?
**Recherche-Basis:** ISO 27001 und BSI C5 nicht öffentlich bestätigt; Auriga verkauft NIS2-Compliance-Dienste, aber eigene Compliance unklar.

- **Gute Antwort:** ISO 27001 zertifiziert + BSI C5 Type II; NIS2-Selbsteinstufung abgeschlossen; Meldung ans BSI erfolgt.
- **Warnsignal:** Keine Zertifizierung; NIS2-Eigenverpflichtung nicht adressiert — Reputationsrisiko („Schuster ohne Schuhe").

### Frage 11: VC-Liquidationspräferenzen & Cap Table

**Frage:** Können Sie den vollständigen Cap Table der Auriga Holding ApS und die Liquidationspräferenzen der VC-Investoren (Lead-Investor, Lead-Investor der Series A, Lead-Investor der Seed-Runde, Co-Investor PE) offenlegen? Gibt es Anti-Dilution-Rechte oder Drag-Along-Klauseln, die den Verkaufsprozess komplizieren könnten?
**Recherche-Basis:** Cap Table und Liquidation Preferences nicht öffentlich bekannt; beeinflussen effektiven Kaufpreis erheblich (LP-Wasserfall).

- **Gute Antwort:** Vollständige Transparenz; LP-Wasserfall für Gründer bei Base-Case-Bewertung attraktiv; keine komplizierenden Drag-Along-Strukturen.
- **Warnsignal:** Starke LP-Strukturen (2×+ Non-Participating) reduzieren Gründer-Erlös; Drag-Along kann Käufer-Flexibilität einschränken.

### Frage 12: CRO-Abgang & Revenue-Leadership

**Frage:** Ex-CRO (CRO) hat im Februar 2026 das Unternehmen verlassen und ist zu Cybersec-Wettbewerber gewechselt. Welche konkreten Auswirkungen hatte ihr Abgang auf die Revenue-Pipeline? Wie ist die aktuelle Rollenverteilung zwischen CBO Chief Business Officer, CCO Ex-GF DE (heute CCO) und dem Vertriebsteam?
**Recherche-Basis:** CRO-Tenure 2,5 Jahre; Abgang Feb 2026; zeitlich nahe an möglichem Runway-Ende; neuer CBO erst seit August 2025.

- **Gute Antwort:** Pipeline-Impact minimal; Chief Business Officer übernimmt nahtlos; Nachfolge ist geplant; kein wesentlicher Umsatzverlust.
- **Warnsignal:** Pipeline-Lücke >15%; Abgang war konfliktbedingt; keine klare Revenue-Leadership mehr.

---

## 13. DD-Roadmap & Nächste Schritte

### 13.1 Offene Informationen (priorisiert)

| Information | Quelle | Kosten | Priorität | Status |
|---|---|---|---|---|
| ARR/MRR-Zahlen 2022–2025 (verifiziert, nach Produkt & Geographie) | Management / VDR | €0 (NDA) | KRITISCH — BLOCKER | Offen |
| Auszug aus DK-CVR Auriga Holding ApS (Cap Table, LP, SHA, Board-Struktur) | CVR Dänemark | ~€30 | KRITISCH — BLOCKER | Offen |
| Hauptrisikoträger-Partnerschaftsvertrag (Laufzeit, CoC-Klausel, Kündigungsfristen) | Management / VDR | €0 (NDA) | KRITISCH — BLOCKER | Offen |
| P&L, Cashflow, Burn-Rate, Runway (letztes vollst. GJ + aktuell) | Management / VDR | €0 (NDA) | KRITISCH | Offen |
| Insurance Combined Ratio / Loss Ratio Auriga Underwriting (2022–2025) | VDR / Aktuariat | €0 (NDA) | KRITISCH | Offen |
| OEM-Verträge CrowdStrike + Microsoft (Laufzeit, CoC, Preisklauseln) | VDR | €0 (NDA) | HOCH | Offen |
| Kundenliste & Konzentration (Top 10/20 nach ARR) | VDR | €0 (NDA) | HOCH | Offen |
| Churn-Rate und Net Revenue Retention (Kohortenanalyse) | VDR | €0 (NDA) | HOCH | Offen |
| IP-Ownership-Struktur + IP-Assignment-Agreements + FTO-Gutachten | VDR / Legal | €0 (NDA) | HOCH | Offen |
| Mitarbeiterliste mit Seniorität, Vergütung, Vesting-Status | VDR | €0 (NDA) | HOCH | Offen |
| Unit Economics: CAC, LTV, Payback Period nach Kanal & Geographie | Management / VDR | €0 (NDA) | HOCH | Offen |
| Jahresabschlüsse Auriga Cybershield ApS (DK) über CVR | CVR Dänemark | ~€30–60 | HOCH | Offen |
| ISO 27001-Zertifikat + BSI-C5-Testat | Management | €0 | MITTEL | Offen |
| BaFin §34d-Registrierung oder EU-Passporting-Nachweis | BaFin / Management | €0 | MITTEL | Offen |
| Wassenaar / Exportkontroll-Assessment für Plattform-Tools | Externer Rechtsberater | €5–15K | MITTEL | Offen |
| EUIPO/DPMA-Markenregistrierungen (Auriga-Marken) | EUIPO.europa.eu | ~€50 | MITTEL | Offen |
| Abgangsumstände beim Cyber-MGA-Wettbewerber Chief Insurance Officer + Chief Underwriting Officer (Drittrecherche) | Referenzchecks | €0–2K | MITTEL | Offen |
| Kununu/Glassdoor-Analyse (eNPS, Fluktuation) | Kununu.com | €0 | NIEDRIG | Offen |

### 13.2 DD-Phasen-Empfehlung

| Phase | Maßnahmen | Timeline | Schwerpunkt |
|---|---|---|---|
| **Phase 1: Pre-DD / Öffentliche Quellen** | CVR DK-Auszüge (€30–90); EUIPO-Markenrecherche; BSI-APT-Status validieren; LinkedIn-Tiefenanalyse; erstes Gespräch CEO Gründer A; Indikatives Interesse kommunizieren; Non-Binding Indicative Offer vorbereiten | Wochen 1–2 | Öffentliche Lücken schließen; Bewertungsrahmen testen |
| **Phase 2: Due Diligence / Data Room** | NDA + VDR-Zugang; Financial DD (3 J. Historik, MRR-Kohortenanalyse, Unit Economics); Insurance DD (separater Aktuar: Loss Ratio, Reserven, Rückversicherung); Legal DD (Gesellschaftsrecht DK, IP, OEM, CoC); Commercial DD (10–15 Referenzkunden, NIS2-Pipeline); Technology DD (SOC-Architektur, Skalierbarkeit, IP-Provenienz); HR DD (Schlüsselpersonen-Interviews, Vesting, Retention-Bereitschaft) | Wochen 3–8 | Kernrisiken quantifizieren; Bewertung verfeinern |
| **Phase 3: Confirmatory DD + Verhandlung** | Management-Präsentation alle Gründer + C-Level; finale Bewertungsabstimmung auf Basis verifizierter Daten; Verhandlung der Earn-Out-KPIs; SPA-Entwurf + Garantienkatalog; Regulatory Approvals (FSA CoC, ggf. BaFin); Hauptrisikoträger-Konsent; Signing & Closing-Planung | Wochen 9–12 | Deal absichern; Dealbreaker-Schwellen final prüfen |

### 13.3 Priorisierte Nächste Schritte

| Priorität | Aktion | Zeithorizont | Verantwortlich |
|---|---|---|---|
| **1 — BLOCKER** | Auszug aus DK-CVR Auriga Holding ApS einholen (Cap Table, LP, SHA) | Sofort (vor LOI) | Legal / M&A-Team |
| **2 — BLOCKER** | Verifizierte Finanzdaten anfordern (ARR, P&L, Cashflow, Burn, Runway) via NDA | Sofort (vor LOI) | Finance / M&A-Team |
| **3 — KRITISCH** | Hauptrisikoträger-Partnerschaftsvertrag (Laufzeit, CoC, Kündigungsfristen) einsehen | Vor LOI oder als LOI-Bedingung | Legal |
| **4 — HOCH** | Management-Meeting mit allen drei Co-Gründern + Dr. Chief Underwriting Officer (CUO) + Chief Insurance Officer (CIO) | Innerhalb 2–3 Wochen | Deal-Team |
| **5 — HOCH** | ISO 27001 / BSI C5-Zertifikate anfordern + BaFin §34d-Status klären | Parallel zu Punkt 4 | Compliance / Legal |
| **6 — MITTEL** | Informational Interview mit Referenzkunden (10–15, via Target oder Channel) | Post-LOI, Phase 2 | Commercial DD |
| **7 — MITTEL** | VC-Investoren-Referenz (Lead-Investor der Series A oder Lead-Investor der Seed-Runde) einholen | Post-LOI | Deal-Team |
| **8 — MITTEL** | Drittrecherche zum Cyber-MGA-Wettbewerber und Referenzcheck für Chief Insurance Officer + Chief Underwriting Officer | Phase 2 | HR / Legal |

### 13.4 DD-Scope nach Workstream-Priorität

| DD-Workstream | Priorität | Begründung |
|---|---|---|
| Financial DD | Höchste | CVR-2023 vorhanden; 2024/2025-Verifikation kritisch für Kaufpreisfindung |
| Legal DD (Verträge) | Höchste | Hauptrisikoträger-Vertrag, Kundenverträge (CoC), IP-Assignment, SHA, OEM-Verträge |
| Insurance / Actuarial DD | Höchste | Auriga Underwriting: Loss Ratio, Reservierung, Hauptrisikoträger-Abhängigkeit, MGA-Profitabilität |
| Tax DD | Hoch | DK-ApS-Struktur, Transfer Pricing DE↔DK, Betriebsstättenrisiko |
| Regulatory / Compliance DD | Hoch | FSA, BaFin, NIS2-Eigenkonformität, DSGVO/AV-Verträge, Wassenaar |
| Commercial DD | Hoch | Kundenkonzentration, Churn, NRR, Pipeline, Wettbewerbsposition |
| HR / People DD | Mittel | Key-Person-Bindung, MA-Fluktuation, Gehaltsstrukturen, Background im Cyber-MGA-Umfeld |
| Technology / IP DD | Mittel | IP-Provenienz aus dem Nachrichtendienst, Plattformarchitektur, Skalierbarkeit, Codebase |

---

## 14. Disclaimer & Datenqualität

**Datenqualitäts-Score: 7 / 10**

Dieses **Beispiel-Briefing** ist eine **fiktive Demonstration** der Struktur des Pre-DD-Briefings. Alle Personen, Firmennamen, Investoren, Vertragspartner und Zahlen sind erfunden und stehen in keinem Zusammenhang mit realen Personen oder Unternehmen.

In einem echten Briefing basieren alle Aussagen auf öffentlich zugänglichen Quellen (NorthData, Bundesanzeiger, KvK/CVR, Handelsregister, Fachpresse, Venture-Datenbanken, regulatorische Register).

Es stellt **KEINE vollständige Due Diligence** dar. Es ersetzt nicht die Prüfung nicht-öffentlicher Daten (Managementberichte, Verträge, interne Systeme, SHA, Cap Table).

Praktisch alle finanziellen Aussagen sind Schätzungen mit erheblichen Konfidenz-Intervallen.

Dieses Briefing wurde mit KI-Unterstützung erstellt und muss von einem M&A-Experten validiert werden.

*Erstellt von salesbriefing.de*`;
}
